import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { DataTable, Btn, Badge } from "@/components/ui-lite";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, FileMinus } from "lucide-react";

function fmtUSD(n) { return n == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n)); }
function currentMonth() { const d = new Date(); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

const STATUS = {
  ok: { tone: "success", icon: <CheckCircle2 size={14} className="inline mr-1"/>, label: "OK to pay" },
  review: { tone: "warning", icon: <AlertTriangle size={14} className="inline mr-1"/>, label: "Review" },
  mismatch: { tone: "danger", icon: <XCircle size={14} className="inline mr-1"/>, label: "Mismatch" },
  missing_invoice: { tone: "info", icon: <FileMinus size={14} className="inline mr-1"/>, label: "No invoice" },
};

export default function TallyPage() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);

  const load = () => api.get(`/tally/${month}`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [month]);

  return (
    <AppShell
      title="Tally"
      subtitle="Match provider invoices against our computed GGR share. Tolerance is configurable in Admin → Reconciliation."
      actions={<input data-testid="tally-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 px-3 wv-input font-mono text-sm"/>}
    >
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        {Object.entries(STATUS).map(([k, meta]) => (
          <div key={k} className="wv-card wv-hover-lift p-6" data-testid={`tally-summary-${k}`}>
            <div className="wv-overline">{meta.label}</div>
            <div className="mt-3 wv-metric-value">{data?.summary?.[k] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Settings hint */}
      {data?.settings && (
        <div className="mb-4 text-xs text-[var(--wv-text-3)] font-mono">
          Tolerance: {data.settings.tally_rule === "min" ? "min" : "max"}( {data.settings.tally_tolerance_pct}% , ${data.settings.tally_tolerance_usd} )
        </div>
      )}

      <DataTable
        testid="tally-table"
        columns={[
          { key: "provider_code", label: "Provider", mono: true },
          { key: "orders", label: "Orders", mono: true },
          { key: "computed_usd", label: "Computed", mono: true, render: (r) => fmtUSD(r.computed_usd) },
          { key: "invoice_usd", label: "Invoice", mono: true, render: (r) => r.invoice_usd ? fmtUSD(r.invoice_usd) : <span className="text-[var(--wv-text-3)]">—</span> },
          { key: "diff_usd", label: "Diff", mono: true, render: (r) => (
            <span className={r.status === "ok" ? "text-[var(--wv-success)]" : r.status === "mismatch" ? "text-[var(--wv-error)] font-medium" : ""}>
              {fmtUSD(r.diff_usd)} {r.diff_pct != null && <span className="text-[var(--wv-text-3)] text-xs">({r.diff_pct > 0 ? "+" : ""}{r.diff_pct}%)</span>}
            </span>
          )},
          { key: "status", label: "Status", render: (r) => {
            const s = STATUS[r.status] || STATUS.review;
            return <Badge tone={s.tone}>{s.icon}{s.label}</Badge>;
          }},
        ]}
        rows={data?.rows || []}
      />
    </AppShell>
  );
}
